import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { API_BASE } from "../config";
import { useSummit } from "../pages/summitdetails";
import {
  RiDownloadLine,
  RiCalendarLine,
  RiMapPinLine,
  RiTimeLine,
  RiUserStarLine,
  RiBuildingLine,
  RiArrowRightLine,
  RiPresentationLine,
  RiCalendarEventLine,
  RiRefreshLine,
  RiAlertLine,
  RiMicLine,
  RiComputerLine,
  RiRestaurantLine,
  RiTrophyLine,
  RiTeamLine,
  RiCheckLine,
} from "react-icons/ri";
import toast from "react-hot-toast";

/* ─── THEME COLORS (Teal & Gold Palette matching Contact page) ───────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
  success: "#2A9D8F",
  warning: "#D4A017",
  danger: "#218577",
};

/* ─── CSS ──────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}

  .t-schedule-wrapper{padding:40px clamp(20px,4vw,64px) 56px;background:${T.offWhite}}
  .t-schedule-container{max-width:1320px;margin:0 auto}
  .t-day-header{display:flex;align-items:center;gap:16px;margin-bottom:24px}
  .t-day-line{height:2px;flex:1;background:linear-gradient(to right,${T.border},transparent)}
  .t-day-line-left{height:2px;flex:1;background:linear-gradient(to left,${T.border},transparent)}
  .t-day-badge{font-size:14px;font-weight:700;color:${T.primary};text-transform:uppercase;letter-spacing:0.08em;background:${T.white};border:1px solid ${T.border};border-radius:40px;padding:8px 24px;box-shadow:${T.shadow}}
  .t-session-card{display:flex;align-items:flex-start;gap:20px;background:${T.white};border:1px solid ${T.border};border-radius:16px;padding:24px 28px;margin-bottom:16px;position:relative;overflow:hidden;box-shadow:${T.shadow};transition:all 0.2s ease}
  .t-session-card:hover{box-shadow:${T.shadowMd};border-color:${T.primary}}
  .t-session-accent{position:absolute;left:0;top:0;bottom:0;width:4px;background:${T.gradPrimary}}
  .t-time-block{min-width:85px;text-align:center;background:${T.softTeal};border-radius:12px;padding:12px 8px;flex-shrink:0}
  .t-time-hour{font-size:18px;font-weight:800;color:${T.primary};line-height:1}
  .t-time-period{font-size:12px;color:${T.textMuted};margin-top:4px}
  .t-session-title{font-size:20px;font-weight:700;color:${T.text};margin:0 0 10px}
  .t-session-desc{font-size:15px;color:${T.textMid};margin:0 0 10px;line-height:1.5}
  .t-session-meta{display:flex;align-items:center;gap:24px;font-size:13px;color:${T.textMuted};flex-wrap:wrap}
  .t-session-meta-item{display:flex;align-items:center;gap:6px}
  .t-session-arrow{display:flex;align-items:center;justify-content:center;color:${T.border};flex-shrink:0;transition:all 0.2s}
  .t-session-card:hover .t-session-arrow{color:${T.primary};transform:translateX(4px)}
  .t-stat-chip{display:flex;align-items:center;gap:10px;background:${T.white};border:1px solid ${T.border};border-radius:40px;padding:10px 24px;box-shadow:${T.shadow}}
  .t-stat-icon{width:36px;height:36px;background:${T.softTeal};border-radius:50%;display:flex;align-items:center;justify-content:center;color:${T.primary}}
  .t-stat-value{font-size:20px;font-weight:800;color:${T.text};line-height:1.2}
  .t-stat-label{font-size:12px;color:${T.textMuted}}
  .t-desc-banner{background:linear-gradient(135deg,${T.softTeal} 0%,${T.white} 100%);border:1px solid ${T.border};border-radius:20px;padding:24px 32px;margin-bottom:40px;position:relative;overflow:hidden}
  .t-desc-banner-bar{position:absolute;top:0;left:0;right:0;height:3px;background:${T.gradPrimary}}
  .t-desc-banner-text{font-size:16px;color:${T.text};margin:0;line-height:1.6}
  
  .t-cta-w{padding:0 clamp(20px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(32px,4vw,52px) clamp(32px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-buttons{display:flex;gap:16px;align-items:center;flex-wrap:wrap}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(20px,4vw,64px);margin-top:40px}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted}}
  
  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:72px 24px;gap:12px}
  .t-s-icon{width:52px;height:52px;border-radius:13px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:${T.primary}}
  .t-s-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:14px;color:${T.textMuted};max-width:340px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:9px 20px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-size:13px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:8px}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}
  .t-download-btn{display:inline-flex;align-items:center;gap:12px;background:${T.gradGold};color:${T.navyDeep};border:none;border-radius:60px;padding:18px 48px;font-size:18px;font-weight:600;cursor:pointer;transition:all 0.3s ease}
  .t-download-btn:hover:not(:disabled){transform:translateY(-3px);box-shadow:${T.shadowGold}}
  .t-download-btn:disabled{opacity:0.5;cursor:not-allowed}
  
  @media (max-width: 768px) {
    .t-cta-buttons { width: 100%; justify-content: center; }
    .t-cta-btn, .t-cta-btn-outline { width: 100%; justify-content: center; }
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

const SessionCard = ({ session }) => {
  const getTypeInfo = (type) => {
    const types = {
      keynote:      { label: "Keynote",      bg: "#E8F5F3", color: "#2A9D8F", icon: RiMicLine },
      workshop:     { label: "Workshop",     bg: "#E8F5E9", color: "#2E7D32", icon: RiComputerLine },
      session:      { label: "Session",      bg: "#EBF1FF", color: "#3B82F6", icon: RiPresentationLine },
      break:        { label: "Break",        bg: "#F5F5F5", color: "#757575", icon: RiRestaurantLine },
      opening:      { label: "Opening",      bg: "#FFF8E1", color: "#D4A017", icon: RiTeamLine },
      plenary:      { label: "Plenary",      bg: "#E8F5F3", color: "#2A9D8F", icon: RiUserStarLine },
      panel:        { label: "Panel",        bg: "#FFF8E1", color: "#D4A017", icon: RiTeamLine },
      closing:      { label: "Closing",      bg: "#E8F5F3", color: "#2A9D8F", icon: RiTrophyLine },
      networking:   { label: "Networking",   bg: "#E3F2FD", color: "#1976D2", icon: RiTeamLine },
    };
    return types[type] || types.session;
  };

  const typeInfo = getTypeInfo(session.type);
  const Icon = typeInfo.icon;
  const timeParts = session.time?.split(" ") || ["TBA", ""];

  return (
    <motion.div className="t-session-card" whileHover={{ y: -2 }}>
      <div className="t-session-accent" />
      <div className="t-time-block">
        <div className="t-time-hour">{timeParts[0]}</div>
        <div className="t-time-period">{timeParts[1] || ""}</div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px", flexWrap: "wrap" }}>
          <h3 className="t-session-title">{session.title}</h3>
          <span style={{ fontSize: "12px", fontWeight: 600, padding: "4px 14px", borderRadius: "30px", background: typeInfo.bg, color: typeInfo.color, display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <Icon size={12} /> {typeInfo.label}
          </span>
        </div>
        {session.description && (
          <p className="t-session-desc">
            {session.description}
            {session.speaker && <span style={{ color: T.primary, fontWeight: 500, marginLeft: "8px" }}>· {session.speaker}</span>}
          </p>
        )}
        {!session.description && session.speaker && (
          <p className="t-session-desc">
            <span style={{ color: T.primary, fontWeight: 500 }}>{session.speaker}</span>
          </p>
        )}
        <div className="t-session-meta">
          {session.affiliation && <span className="t-session-meta-item"><RiBuildingLine size={14} /> {session.affiliation}</span>}
          {session.location && <span className="t-session-meta-item"><RiMapPinLine size={14} /> {session.location}</span>}
        </div>
      </div>
      <div className="t-session-arrow"><RiArrowRightLine size={20} /></div>
    </motion.div>
  );
};

const StatChip = ({ icon: Icon, label, value }) => (
  <div className="t-stat-chip">
    <div className="t-stat-icon"><Icon size={18} /></div>
    <div>
      <div className="t-stat-value">{value}</div>
      <div className="t-stat-label">{label}</div>
    </div>
  </div>
);

const Spinner = () => (
  <div style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
);

const SchedulePage = () => {
  let summitContext = null;
  try { summitContext = useSummit(); } catch (_) {}

  const { id: paramId } = useParams();

  const eventId = summitContext?.eventId
    || summitContext?.summit?._id
    || summitContext?.summit?.id
    || paramId;

  const [activeSchedule, setActiveSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (eventId) {
      fetchActiveSchedule();
    } else {
      setError("No event specified.");
      setLoading(false);
    }
  }, [eventId]);

  const fetchActiveSchedule = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/schedule/active/${eventId}`;
      console.log("Fetching schedule, eventId:", eventId, "url:", url);

      const { data } = await axios.get(url);

      if (data.success && data.data) {
        setActiveSchedule(data.data);
      } else {
        setError("No active schedule found for this event.");
      }
    } catch (err) {
      console.error("Error fetching schedule:", err);
      if (err.response?.status === 404) {
        setError("No schedule has been published for this event yet.");
      } else {
        setError("Failed to load schedule. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!activeSchedule || !activeSchedule.schedule?.length) {
      toast.error("No schedule data available to download");
      return;
    }
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/schedule/generate-pdf`,
        { scheduleId: activeSchedule._id, eventId },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `schedule-${activeSchedule.edition || "conference"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF error:", err);
      toast.error("Error generating PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const scheduleItems = activeSchedule?.schedule || [];
  const groupedByDay = scheduleItems.reduce((acc, item) => {
    const day = item.day || "Schedule";
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {});
  Object.keys(groupedByDay).forEach(day => {
    groupedByDay[day].sort((a, b) => (a.order || 0) - (b.order || 0));
  });
  const days = Object.entries(groupedByDay).map(([day, sessions]) => ({ day, sessions }));
  const totalSessions = scheduleItems.length;
  const uniqueSpeakers = new Set(scheduleItems.map(i => i.speaker).filter(Boolean)).size;

  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiCalendarEventLine size={13} /> Conference Program</div>
              <h1 className="t-hero-h">Conference <em>Schedule</em></h1>
            </div>
          </div>
        </div>
        <div className="t-schedule-wrapper">
          <div className="t-schedule-container" style={{ textAlign: "center", padding: "80px 20px" }}>
            <Spinner />
            <p style={{ color: T.textMuted }}>Loading schedule...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !activeSchedule) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiCalendarEventLine size={13} /> Conference Program</div>
              <h1 className="t-hero-h">Conference <em>Schedule</em></h1>
              <p className="t-hero-p">Explore the full conference program, including keynote presentations, technical sessions, workshops, and networking events.</p>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiAlertLine size={24} /></div>
          <p className="t-s-title">Schedule Unavailable</p>
          <p className="t-s-sub">{error || "Unable to load schedule."}</p>
          <button className="t-s-btn" onClick={fetchActiveSchedule}>
            <RiRefreshLine size={15} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ── HERO SECTION (Matching Contact page) ── */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiCalendarEventLine size={13} />
              Meetings21 · Conference Program
            </div>
            <h1 className="t-hero-h">
              Conference <em>Schedule</em>
            </h1>
            <p className="t-hero-p">
              Explore the full conference program, including keynote presentations, 
              technical sessions, workshops, and networking events.
            </p>
            {/* Decorative line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{activeSchedule.edition || "—"}</span>
              <span className="t-st-l">Edition</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{days.length}</span>
              <span className="t-st-l">Days</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{totalSessions}</span>
              <span className="t-st-l">Sessions</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <div className="t-schedule-wrapper">
        <div className="t-schedule-container">

          {/* Stat chips */}
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap", marginBottom: "32px" }}>
            {activeSchedule.venue && (
              <StatChip icon={RiMapPinLine} label="Venue" value={activeSchedule.venue.split(",")[0]} />
            )}
            {activeSchedule.dateRange && (
              <StatChip icon={RiCalendarLine} label="Date" value={activeSchedule.dateRange} />
            )}
            <StatChip icon={RiTimeLine} label="Sessions" value={totalSessions} />
            {uniqueSpeakers > 0 && (
              <StatChip icon={RiUserStarLine} label="Speakers" value={uniqueSpeakers} />
            )}
          </div>

          {/* Title + description banner */}
          <div className="t-desc-banner">
            <div className="t-desc-banner-bar" />
            <p className="t-desc-banner-text" style={{ fontWeight: 700, fontSize: 18, marginBottom: activeSchedule.description ? 8 : 0 }}>
              {activeSchedule.title}
            </p>
            {activeSchedule.description && (
              <p className="t-desc-banner-text">{activeSchedule.description}</p>
            )}
          </div>

          {/* Schedule by day */}
          {days.length > 0 ? (
            days.map((day, di) => (
              <div key={di} style={{ marginBottom: "40px" }}>
                <div className="t-day-header">
                  <div className="t-day-line-left" />
                  <div className="t-day-badge">
                    <RiCalendarLine size={14} style={{ marginRight: "8px" }} />
                    {day.day}
                  </div>
                  <div className="t-day-line" />
                </div>
                {day.sessions.map((session, si) => (
                  <SessionCard key={si} session={session} />
                ))}
              </div>
            ))
          ) : (
            <div className="t-sw" style={{ background: T.white, borderRadius: 20, padding: 60 }}>
              <div className="t-s-icon"><RiCalendarLine size={28} /></div>
              <p className="t-s-title">Sessions coming soon</p>
              <p className="t-s-sub">Session details will be added shortly.</p>
            </div>
          )}

          {/* Download button */}
          {totalSessions > 0 && (
            <div style={{ textAlign: "center", paddingTop: "32px", marginTop: "20px", borderTop: `1px solid ${T.border}` }}>
              <button onClick={downloadPDF} disabled={downloading} className="t-download-btn">
                {downloading ? (
                  <>
                    <div style={{ width: 20, height: 20, border: "2px solid rgba(212,160,23,0.4)", borderTopColor: T.navyDeep, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    Generating PDF...
                  </>
                ) : (
                  <><RiDownloadLine size={22} /> Download Schedule PDF</>
                )}
              </button>
              <p style={{ fontSize: 13, color: T.textMuted, marginTop: 16 }}>
                Includes complete session details, speaker information, and venue
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── CTA SECTION (Matching Contact page) ── */}
      <div className="t-cta-w">
        <motion.div className="t-cta" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
          <div>
            <div className="t-cta-tag">Register Now</div>
            <h2 className="t-cta-h">Ready to <span style={{ color: T.accentGold }}>Join</span> the Summit?</h2>
            <p className="t-cta-p">Register now for the premier academic conference of 2026 and connect with scholars worldwide.</p>
          </div>
          <div className="t-cta-buttons">
            <Link to={`/register?eventId=${eventId}`} className="t-cta-btn">Register Now <RiArrowRightLine size={15} /></Link>
            <Link to="/about" className="t-cta-btn-outline">Learn About Us <RiArrowRightLine size={15} /></Link>
          </div>
        </motion.div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="t-footer">
        <div className="t-fi">
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 900 }}>
            <span style={{ color: T.primary }}>Meeting</span>
            <span style={{ color: T.accentGold }}>21</span>
          </div>
          <span className="t-f-copy">© 2025 Meetings21 · International Academic Conferences · All rights reserved</span>
        </div>
      </footer>
    </div>
  );
};

export default SchedulePage;